import {groupBy} from 'lodash';
import {Car, Company, Order, OrderPriority, Proposal, SuplRequest, SuplStore} from './base.model';
import {availabilityLabelMap, keyboard_en, keyboard_ru, NO_CLIENT_COMPANY_NAME, URL_PART_LIVE} from './constants';

// A field the form left empty, as opposed to a value that is merely falsy. The
// distinction matters when stripping blanks out of an RPC payload: `false` is a
// real answer and must survive, while `omitBy(payload, (v) => !v)` eats it. That
// is exactly how `answerRequired: false` used to disappear on its way to the
// backend, in two separate services.
export const isBlank = (v: unknown): boolean => v === null || v === undefined || v === '';

// Resolve the "Без клиента" house company id from the loaded company list. This is the
// single place the sentinel name is referenced; all client-less logic keys off the id.
export function getHouseCompanyId(companies: Company[]): number {
    const houseCompany = (companies || []).find((company) => company.name === NO_CLIENT_COMPANY_NAME);

    return houseCompany ? houseCompany.id : null;
}

// An order has no real client when it belongs to the house company, has no company id, or
// its company is not resolvable in the loaded list. The last case covers the lazy window
// right after the first client-less order, when the cached company list does not yet
// contain the newly-created house company (so houseCompanyId is still null).
export function isClientNotConfigured(companyId: number, houseCompanyId: number, company: Company): boolean {
    return !companyId || companyId === houseCompanyId || !company;
}

// An order has no car configured when none of its part-requests references a car.
export function isCarNotConfigured(order: Order): boolean {
    return !!order && !order.suplRequests.some((suplRequest) => !!suplRequest.carID);
}

// Does this position still want an answer from IDCar staff?
//
// "Не отвечать" is now one of three statuses rather than a separate flag, so
// this asks the status. Absent still means yes: the column defaults to
// ...Normal, and orders cached from before it existed carry no value at all.
export function needsAnswer(suplRequest: SuplRequest): boolean {
    return suplRequest.processingPriority !== OrderPriority.low;
}

// "Nobody need answer this order" is DERIVED — every one of its positions is
// silent — and is deliberately not stored anywhere. The order's own
// processingPriority is a DEFAULT for positions added later, never a summary of
// the ones already there; past creation
// it decides nothing, so reading the order-level state off the priority would
// start lying the moment a single position is put back into answering.
//
// An order with no positions is not "answered"; it is empty. Say false.
export function isAnswerNotRequired(order: Order): boolean {
    return !!order
        && order.suplRequests.length > 0
        && !order.suplRequests.some(needsAnswer);
}

// An order's approved proposal may be linked to a waybill product only when the position has
// a real client (not the house company) and a cartype. The car is not required: most approved
// positions have none, and the backend createMatch/createMatches accept them. Either missing field blocks
// linking — a waybill is an accounting document and must be attributable. Returns the i18n keys
// of the missing fields (empty when fully configured).
// NOTE: client detection keys off houseCompanyId; during the lazy window right after the
// first client-less order (houseCompanyId still null), a house-company order may slip
// through here — the backend createMatch/createMatches guard is the authoritative backstop for that case.
export function getOrderLinkBlockers(suplRequest: SuplRequest, houseCompanyId: number): string[] {
    if (!suplRequest) {
        return [];
    }

    const blockers: string[] = [];

    if (!suplRequest.companyID || suplRequest.companyID === houseCompanyId) {
        blockers.push('label_field_client');
    }

    if (!suplRequest.carType) {
        blockers.push('label_field_cartype');
    }

    return blockers;
}

export function isOrderLinkable(suplRequest: SuplRequest, houseCompanyId: number): boolean {
    return getOrderLinkBlockers(suplRequest, houseCompanyId).length === 0;
}

export interface Deferred<T> {
    promise: Promise<T>;
    resolve: (data?: any) => any;
    reject: (data?: any) => any;
}

export const getValue = (obj: any, keyPath: string): any => {
    const path = keyPath.split('.');
    let value = obj;

    path.forEach((key) => {
        if (value) {
            value = value[key];
        }
    });

    return value;
};

export const arrayToMap = (array: any[], key: string) => {
    return array.reduce((obj, item) => {
        obj[getValue(item, key)] = item;

        return obj;
    }, {});
};

export const createDeferredObj = (): Deferred<any> => {
    const defer: Deferred<any> = {
        promise: null,
        resolve: null,
        reject: null
    };

    defer.promise = new Promise((resolve, reject) => {
        defer.resolve = resolve;
        defer.reject = reject;
    });

    return defer;
};

// The texts of `label_car_removed_suffix` and `label_car_removed` in ru.json, the panel's only locale: car names
// are built here, in plain functions that templates call, with no TranslateService to ask.
const CAR_REMOVED_SUFFIX = ' (удалена)';
const CAR_REMOVED = 'Авто удалено';

export const getCarName = (car: Car): string => {
    let name = '';

    if (car) {
        if (car.title) {
            name = car.title;
        } else if (car.brand || car.model) {
            name = `${car.brand} ${car.model}`;

            if (car.registration) {
                name += ` (${car.registration})`;
            }
        } else if (car.registration) {
            name = car.registration;
        } else {
            name = car.vin;
        }

        if (car.removedAt) {
            name = `${name ?? ''}${CAR_REMOVED_SUFFIX}`;
        }
    }

    return name;
};

// An order line may name a car that is in no client's list at all.
export const carNameById = (cars: Car[], carId: number): string => {
    const car = (cars || []).find((c) => c.id === carId);

    return car ? getCarName(car) : CAR_REMOVED;
};

export const getVersion = (): string => {
    const hostname = document.location.hostname;
    let version = '';

    if (hostname) {
        const firstPart = hostname.split('.')[0];

        if (firstPart !== URL_PART_LIVE) {
            version = firstPart;
        }
    }

    return version;
};

export const convertInputSource = (text: string): string => {
    return text.split('').map((c) => {
        let index = keyboard_en.indexOf(c);

        if (index > -1) {
            return keyboard_ru[index];
        } else {
            index = keyboard_ru.indexOf(c);

            return (index > -1) ? keyboard_en[index] : c;
        }
    }).join('');
};

export const getSuplStoreNameById = (suplStoreId: number, suplStores: SuplStore[]): string => {
    const store = suplStores.find((s) => s.id === suplStoreId);

    return store ? store.name : '';
};

export const getProposalsText = (suplRequest: SuplRequest,
                                 proposals: Proposal[],
                                 suplStores: SuplStore[],
                                 translations: Record<string, string>,
                                 isPriceIncluded = false): string => {
    const proposalsData = groupBy(proposals, 'suplStoreID');
    let isFirst = true;
    let text = '';

    text += `${suplRequest.text.trim()}\n`;
    text += '********************\n';

    for (const suplStoreId in proposalsData) {
        if (!isFirst) {
            text += '---------------\n';
        }

        text += `${getSuplStoreNameById(parseInt(suplStoreId, 10), suplStores)}\n`;
        text += `${proposalsData[suplStoreId].length
            ? proposalsData[suplStoreId].map((p) => {
                const proposalPrice = parseFloat(p.margin) > 0 && isPriceIncluded
                                    ? `${convertToIntegerString(p.price)} + ${convertToIntegerString(p.margin)}% = ${convertToIntegerString(p.priceWithMargin)}`
                                    : convertToIntegerString(p.priceWithMargin) || convertToIntegerString(p.price) || '';

                let proposalText = `${p.text.trim()} ${proposalPrice}`;

                if (p.quantity) {
                    proposalText += ` ${p.quantity}${translations['text_quantity_unit']}`;
                }

                proposalText += p.stock
                    ? p.stock.period
                        ? ` ${translations[availabilityLabelMap[p.stock.availability + p.stock.period]]}`
                        : ` ${translations[availabilityLabelMap[p.stock.availability]]}`
                    : '';

                return proposalText.trim();
            }).join('\n')
            : ''}\n`;

        isFirst = false;
    }

    return text;
};

export const getCompaniesAndCars = (companiesWithCars: { company: Company, cars: Car[] }[]): { companies: Company[], cars: Car[] } => {
    const companies = [];
    const cars = [];

    companiesWithCars.forEach((companyWithCars) => {
        companies.push(companyWithCars.company);
        cars.push(...companyWithCars.cars);
    });

    return { companies, cars };
};

export const convertToIntegerString = (numberStr: string): string => {
    let result = '';
    const num = parseInt(numberStr, 10);

    if (!isNaN(num)) {
        result = num.toString();
    }

    return result;
};

export const getManagersFromOrders = (orders: Order[]): { id: number, name: string }[] => {
    const managers = [];

    orders.forEach((order) => {
        if (order.suplRequests.length) {
            const suplRequest = order.suplRequests[0];

            if (suplRequest && !managers.find((m) => m.id === suplRequest.createdBy)) {
                managers.push({
                    id: suplRequest.createdBy,
                    name: suplRequest.createdByUsername
                });
            }
        }
    });

    return managers;
};

export const marginPercentToUah = (price: number | string, percent: number | string): number => {
    const p = Number(price);
    const m = Number(percent);

    return p > 0 && m ? Math.round(p * m) / 100 : 0;
};

export const marginUahToPercent = (price: number | string, uah: number | string): number => {
    const p = Number(price);
    const u = Number(uah);

    return p > 0 && u ? Math.round(u / p * 100 * 100) / 100 : 0;
};

const isBlankMargin = (value: number | string): boolean => value === '' || value === null || value === undefined;

export const syncMarginUah = (price: number | string, percent: number | string): number | string => {
    return isBlankMargin(percent) ? '' : marginPercentToUah(price, percent);
};

export const syncMarginPercent = (price: number | string, uah: number | string): number | string => {
    return isBlankMargin(uah) ? '' : marginUahToPercent(price, uah);
};

// The three statuses, in the order the desk reads them, with the icon each one
// wears. Shared rather than duplicated: the creation form, the edit form and the
// order page all offer the same list, and a fourth status must appear in all
// three at once or in none.
export const ORDER_PRIORITY_OPTIONS: { id: OrderPriority, label: string }[] = [
    {id: OrderPriority.low, label: 'label_priority_low'},
    {id: OrderPriority.normal, label: 'label_priority_normal'},
    {id: OrderPriority.high, label: 'label_priority_high'}
];

// Mirrors the symbols the Telegram message uses (⚪/🟡/🔴) in Material icons, so
// a line reads the same on the board as it does in the notification.
export function priorityIcon(priority: OrderPriority): string {
    switch (priority) {
        case OrderPriority.low:
            return 'notifications_off';
        case OrderPriority.high:
            return 'priority_high';
        default:
            return 'notifications_active';
    }
}

// Only "не отвечать" and "срочный" are worth colouring; ordinary is the norm and
// a page where everything is coloured says nothing.
export function priorityColor(priority: OrderPriority): string {
    return priority === OrderPriority.normal ? '' : 'warn';
}
